import React, { useEffect, useState } from 'react';
import { useSourcing } from '../../contexts/SourcingContext';
import { ClockIcon, CheckCircleIcon, UserIcon, BarChart3Icon, ThumbsUpIcon, MailIcon, PhoneIcon, PlusIcon, XIcon, FileTextIcon, AlertTriangleIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon, ZapIcon, CheckIcon, ExternalLinkIcon, InfoIcon, XCircleIcon } from 'lucide-react';
const EventDashboard: React.FC = () => {
  const {
    sourcingEvent,
    updateEventStatus
  } = useSourcing();
  const [currentStage, setCurrentStage] = useState<'collecting' | 'evaluation' | 'award'>('collecting');
  const [expandedTable, setExpandedTable] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<any>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierEmail, setNewSupplierEmail] = useState('');
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [selectedRiskSupplier, setSelectedRiskSupplier] = useState<any>(null);
  // Add state to store supplier data consistently
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [bestBid, setBestBid] = useState<any>(null);
  // Add new state for negotiation
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);
  const [negotiationStage, setNegotiationStage] = useState<'initial' | 'progress' | 'complete'>('initial');
  const [negotiationResults, setNegotiationResults] = useState<{
    originalPrice: number;
    negotiatedPrice: number;
    discount: number;
    additionalTerms: string[];
    accepted: boolean | null;
  } | null>(null);
  // Generate supplier data once on component mount
  useEffect(() => {
    if (!sourcingEvent) return;
    // Create deterministic supplier data
    const generatedSuppliers = sourcingEvent.suppliers.filter(supplier => supplier.selected).map((supplier, index) => {
      // Use supplier ID as a simple seed for "random" but consistent values
      const seed = supplier.id.charCodeAt(0) + index;
      const statusSeed = seed % 10;
      // More realistic laptop prices (€800-€2000 range)
      const priceSeed = seed % 1200 + 800;
      // Get quantity from sourcing event or default to 100
      const quantity = sourcingEvent.requirements.quantity || 100;
      // Calculate total cost as quantity * unit price
      const totalCost = priceSeed * quantity;
      return {
        ...supplier,
        status: statusSeed > 3 ? 'submitted' : 'viewed',
        bid: {
          unitPrice: priceSeed,
          totalCost: totalCost,
          deliveryDate: new Date(Date.now() + seed % 30 * 24 * 60 * 60 * 1000),
          warranty: ['1 year', '2 years', '6 months'][seed % 3],
          proposalFile: `proposal-${supplier.id}.pdf`,
          benefits: ['High quality components', 'Established supply chain', 'Strong technical support'][seed % 3],
          threats: ['Longer lead times', 'Limited customization options', 'Higher upfront costs'][seed % 3],
          riskLevel: ['Low', 'Medium', 'High'][seed % 3],
          riskDetails: {
            financial: ['Low', 'Medium', 'High'][seed % 3],
            delivery: ['Low', 'Medium', 'High'][(seed + 1) % 3],
            quality: ['Low', 'Medium', 'High'][(seed + 2) % 3],
            compliance: ['Low', 'Medium', 'High'][(seed + 3) % 3],
            analysis: ['Supplier has strong financial stability and consistent delivery history.', 'Some concerns about supply chain resilience but overall acceptable risk profile.', 'Multiple risk factors identified including financial instability and quality concerns.'][seed % 3]
          }
        }
      };
    });
    setSuppliers(generatedSuppliers);
    // Find the best bid once
    if (generatedSuppliers.length > 0) {
      const best = generatedSuppliers.reduce((best, current) => {
        if (!best || (current.bid?.unitPrice || 0) < (best.bid?.unitPrice || 0)) {
          return current;
        }
        return best;
      }, generatedSuppliers[0]);
      setBestBid(best);
    }
  }, [sourcingEvent]);
  if (!sourcingEvent) {
    return <div data-id="element-98">No active sourcing event. Please start a new one.</div>;
  }
  const completedBids = suppliers.filter(s => s.status === 'submitted').length;
  const totalSuppliers = suppliers.length;
  const progressPercentage = completedBids / totalSuppliers * 100;
  const handleCompleteCollection = () => {
    setCurrentStage('evaluation');
    console.log('Switched to evaluation stage');
  };
  const handleAward = (supplierId: string) => {
    updateEventStatus('awarded');
    setCurrentStage('award');
    setSelectedSupplier(supplierId);
  };
  const handleViewProposal = (supplier: any) => {
    setSelectedProposal(supplier);
    setShowProposalModal(true);
  };
  const handleAddSupplier = () => {
    if (!newSupplierName.trim()) return;
    // In a real app, we would add the supplier to the context
    // and refresh the suppliers list
    setShowSupplierModal(false);
    setNewSupplierName('');
    setNewSupplierEmail('');
  };
  const handleRemoveSupplier = (supplierId: string) => {
    // In a real app, we would remove the supplier from the context
    // and refresh the suppliers list
  };
  const handleViewRiskDetails = (supplier: any) => {
    setSelectedRiskSupplier(supplier);
    setShowRiskModal(true);
  };
  const getRiskBadgeColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'Low':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'High':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'Low':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-99" />;
      case 'Medium':
        return <AlertTriangleIcon size={16} className="text-yellow-500" data-id="element-100" />;
      case 'High':
        return <XCircleIcon size={16} className="text-red-500" data-id="element-101" />;
      default:
        return <InfoIcon size={16} className="text-gray-500" data-id="element-102" />;
    }
  };
  const handleStartNegotiation = () => {
    setShowNegotiationModal(true);
    setNegotiationStage('initial');
    // Reset negotiation results
    if (bestBid) {
      setNegotiationResults({
        originalPrice: bestBid.bid.unitPrice,
        negotiatedPrice: 0,
        discount: 0,
        additionalTerms: [],
        accepted: null
      });
    }
  };
  const simulateNegotiation = () => {
    setNegotiationStage('progress');
    // Simulate AI negotiation process with a timer
    setTimeout(() => {
      if (bestBid) {
        // Calculate a deterministic discount based on the bestBid's unitPrice
        const discountPercent = 5; // Fixed at 5% for consistency
        const originalPrice = bestBid.bid.unitPrice;
        const discount = Math.round(originalPrice * discountPercent / 100);
        const negotiatedPrice = originalPrice - discount;
        // Fixed additional terms for consistency
        const additionalTerms = ['Extended warranty from 1 year to 2 years', 'Priority support for 6 months'];
        // Update negotiation results
        setNegotiationResults({
          originalPrice,
          negotiatedPrice,
          discount,
          additionalTerms,
          accepted: null
        });
        setNegotiationStage('complete');
      }
    }, 3000); // Simulate 3 seconds of negotiation
  };
  const handleAcceptNegotiation = () => {
    if (negotiationResults && bestBid) {
      // Update the bid with negotiated terms
      const updatedBid = {
        ...bestBid,
        bid: {
          ...bestBid.bid,
          unitPrice: negotiationResults.negotiatedPrice,
          negotiatedTerms: negotiationResults.additionalTerms
        }
      };
      // In a real app, we would update the supplier's bid in the context
      // For now, just update the local state to show it was accepted
      setNegotiationResults({
        ...negotiationResults,
        accepted: true
      });
      // Close the modal after a delay
      setTimeout(() => {
        setShowNegotiationModal(false);
      }, 1500);
    }
  };
  const handleDeclineNegotiation = () => {
    if (negotiationResults) {
      setNegotiationResults({
        ...negotiationResults,
        accepted: false
      });
      // Close the modal after a delay
      setTimeout(() => {
        setShowNegotiationModal(false);
      }, 1500);
    }
  };
  return <div className="flex flex-col h-[calc(100vh-120px)]" data-id="element-103">
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4" data-id="element-104">
        <h1 className="text-xl font-semibold text-gray-800" data-id="element-105">
          {sourcingEvent.requirements.title}
        </h1>
        <p className="text-gray-600 mt-1" data-id="element-106">
          {currentStage === 'collecting' && 'Collecting supplier proposals'}
          {currentStage === 'evaluation' && 'Evaluating supplier proposals'}
          {currentStage === 'award' && 'Sourcing event completed'}
        </p>
        <div className="text-xs text-gray-400 mt-1" data-id="element-107">
          Current stage: {currentStage}
        </div>
      </div>
      {/* Event Timeline - Updated to match the provided design */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4" data-id="element-108">
        <div className="flex items-center justify-between" data-id="element-109">
          <div className="flex flex-col items-center" data-id="element-110">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStage === 'collecting' || currentStage === 'evaluation' || currentStage === 'award' ? 'bg-blue-600 text-white' : 'border-2 border-gray-300 text-gray-500'}`} data-id="element-111">
              {currentStage === 'collecting' || currentStage === 'evaluation' || currentStage === 'award' ? <CheckIcon size={20} data-id="element-112" /> : <span className="font-medium" data-id="element-113">1</span>}
            </div>
            <span className="text-sm font-medium mt-2" data-id="element-114">Submit Proposal</span>
          </div>
          <div className={`h-1 flex-1 mx-2 ${currentStage === 'evaluation' || currentStage === 'award' ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-115"></div>
          <div className="flex flex-col items-center" data-id="element-116">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStage === 'evaluation' || currentStage === 'award' ? 'bg-blue-600 text-white' : 'border-2 border-gray-300 text-gray-500'}`} data-id="element-117">
              {currentStage === 'evaluation' || currentStage === 'award' ? <CheckIcon size={20} data-id="element-118" /> : <span className="font-medium" data-id="element-119">2</span>}
            </div>
            <span className="text-sm font-medium mt-2" data-id="element-120">Verify Details</span>
          </div>
          <div className={`h-1 flex-1 mx-2 ${currentStage === 'award' ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-121"></div>
          <div className="flex flex-col items-center" data-id="element-122">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStage === 'award' ? 'bg-blue-600 text-white' : 'border-2 border-gray-300 text-gray-500 bg-white'}`} data-id="element-123">
              {currentStage === 'award' ? <CheckIcon size={20} data-id="element-124" /> : <span className="font-medium" data-id="element-125">3</span>}
            </div>
            <span className="text-sm font-medium mt-2" data-id="element-126">
              Submission Complete
            </span>
          </div>
        </div>
      </div>
      {/* REBUILT: Comparative Bids Table */}
      <div className="bg-white rounded-lg shadow-sm mb-4" data-id="element-127">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-128">
          <h2 className="font-semibold" data-id="element-129">Comparative Bids</h2>
          <div className="flex items-center space-x-2" data-id="element-130">
            <div className="text-xs text-gray-500" data-id="element-131">
              {expandedTable ? 'Full view' : 'Compact view'}
            </div>
            <button className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50" onClick={() => setExpandedTable(!expandedTable)} title={expandedTable ? 'Collapse table' : 'Expand table'} data-id="element-132">
              {expandedTable ? <ChevronUpIcon size={18} data-id="element-133" /> : <ChevronDownIcon size={18} data-id="element-134" />}
            </button>
          </div>
        </div>
        {suppliers && suppliers.length > 0 ? <div className={`relative ${expandedTable ? 'h-[calc(100vh-400px)]' : 'h-[300px]'}`} data-id="element-135">
            <div className="absolute inset-0 overflow-auto" data-id="element-136">
              <table className="w-full border-collapse" data-id="element-137">
                <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-138">
                  <tr data-id="element-139">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-140">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-141">
                      Unit Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-142">
                      Total Cost
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-143">
                      Delivery
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-144">
                      Warranty
                    </th>
                    {expandedTable && <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-145">
                          Benefits/Threats
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-146">
                          Risk Assessment
                        </th>
                      </>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-147">
                  {suppliers.map(supplier => <tr key={supplier.id} className="hover:bg-gray-50" data-id="element-148">
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-149">
                        <div className="text-sm font-medium text-gray-900" data-id="element-150">
                          {supplier.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-151">
                        {currentStage === 'evaluation' || currentStage === 'award' ? <div className={`text-sm ${supplier.id === bestBid?.id ? 'font-bold text-green-600' : 'text-gray-800'}`} data-id="element-152">
                            €{supplier.bid?.unitPrice}
                          </div> : <div className="text-sm text-gray-400" data-id="element-153">Pending</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-154">
                        {currentStage === 'evaluation' || currentStage === 'award' ? <div className="text-sm text-gray-800" data-id="element-155">
                            €{supplier.bid?.totalCost?.toLocaleString()}
                          </div> : <div className="text-sm text-gray-400" data-id="element-156">Pending</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-157">
                        {currentStage === 'evaluation' || currentStage === 'award' ? <div className="text-sm text-gray-800" data-id="element-158">
                            {supplier.bid?.deliveryDate?.toLocaleDateString()}
                          </div> : <div className="text-sm text-gray-400" data-id="element-159">Pending</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-160">
                        {currentStage === 'evaluation' || currentStage === 'award' ? <div className="text-sm text-gray-800" data-id="element-161">
                            {supplier.bid?.warranty}
                          </div> : <div className="text-sm text-gray-400" data-id="element-162">Pending</div>}
                      </td>
                      {expandedTable && <>
                          <td className="px-4 py-3" data-id="element-163">
                            {currentStage === 'evaluation' || currentStage === 'award' ? <div className="text-sm" data-id="element-164">
                                <div className="flex items-start" data-id="element-165">
                                  <ThumbsUpIcon size={14} className="text-green-500 mt-0.5 mr-1 flex-shrink-0" data-id="element-166" />
                                  <span className="text-green-700" data-id="element-167">
                                    {supplier.bid?.benefits}
                                  </span>
                                </div>
                                <div className="flex items-start mt-1" data-id="element-168">
                                  <AlertTriangleIcon size={14} className="text-orange-500 mt-0.5 mr-1 flex-shrink-0" data-id="element-169" />
                                  <span className="text-orange-700" data-id="element-170">
                                    {supplier.bid?.threats}
                                  </span>
                                </div>
                              </div> : <div className="text-sm text-gray-400" data-id="element-171">
                                Pending
                              </div>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap" data-id="element-172">
                            {currentStage === 'evaluation' || currentStage === 'award' ? <button className="flex items-center hover:bg-gray-50 p-1 rounded-md transition-colors" onClick={() => handleViewRiskDetails(supplier)} title="View risk assessment details" data-id="element-173">
                                <ShieldCheckIcon size={16} className={`mr-1 ${supplier.bid?.riskLevel === 'Low' ? 'text-green-500' : supplier.bid?.riskLevel === 'Medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-174" />
                                <span className={`text-xs px-2 py-1 rounded-full ${getRiskBadgeColor(supplier.bid?.riskLevel)}`} data-id="element-175">
                                  {supplier.bid?.riskLevel} Risk
                                </span>
                                <InfoIcon size={14} className="ml-1 text-gray-400" data-id="element-176" />
                              </button> : <div className="text-sm text-gray-400" data-id="element-177">
                                Pending
                              </div>}
                          </td>
                        </>}
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div> : <div className="flex items-center justify-center h-64" data-id="element-178">
            <p className="text-gray-500" data-id="element-179">No supplier data available</p>
          </div>}
      </div>
      {/* Modified: Place the supplier status and AI recommendation side by side */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-180">
        {/* Left column - Supplier Status */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-181">
          <div className="p-4 border-b border-gray-200" data-id="element-182">
            <h2 className="font-semibold" data-id="element-183">Supplier Status</h2>
          </div>
          <div className="p-4" data-id="element-184">
            <div className="mb-4" data-id="element-185">
              <div className="flex justify-between items-center mb-1" data-id="element-186">
                <span className="text-sm text-gray-600" data-id="element-187">
                  Responses received
                </span>
                <span className="text-sm font-medium" data-id="element-188">
                  {completedBids} of {totalSuppliers}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-189">
                <div className="bg-blue-600 h-2 rounded-full" style={{
                width: `${progressPercentage}%`
              }} data-id="element-190"></div>
              </div>
            </div>
            <div className="space-y-3 mt-6" data-id="element-191">
              {suppliers.map(supplier => <div key={supplier.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-md" data-id="element-192">
                  <div className="flex items-center" data-id="element-193">
                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-194">
                      <UserIcon size={16} className="text-gray-600" data-id="element-195" />
                    </div>
                    <div className="ml-3" data-id="element-196">
                      <p className="text-sm font-medium" data-id="element-197">{supplier.name}</p>
                      <div className="flex items-center mt-1" data-id="element-198">
                        {supplier.status === 'submitted' && (currentStage === 'evaluation' || currentStage === 'award') ? <>
                            <CheckCircleIcon size={14} className="text-green-500 mr-1" data-id="element-199" />
                            <span className="text-xs text-green-600" data-id="element-200">
                              Bid submitted
                            </span>
                          </> : <>
                            <ClockIcon size={14} className="text-yellow-500 mr-1" data-id="element-201" />
                            <span className="text-xs text-yellow-600" data-id="element-202">
                              Viewed invitation
                            </span>
                          </>}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-203">
                    {supplier.status === 'submitted' && (currentStage === 'evaluation' || currentStage === 'award') && <button className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50" onClick={() => handleViewProposal(supplier)} title="View proposal" data-id="element-204">
                          <FileTextIcon size={16} data-id="element-205" />
                        </button>}
                    <button className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50" data-id="element-206">
                      <MailIcon size={16} data-id="element-207" />
                    </button>
                    <button className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50" data-id="element-208">
                      <PhoneIcon size={16} data-id="element-209" />
                    </button>
                    <button className="p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-50" onClick={() => handleRemoveSupplier(supplier.id)} title="Remove supplier" data-id="element-210">
                      <XIcon size={16} data-id="element-211" />
                    </button>
                  </div>
                </div>)}
            </div>
            {/* Add Supplier Button */}
            <button className="w-full mt-4 py-2 flex items-center justify-center bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" onClick={() => setShowSupplierModal(true)} data-id="element-212">
              <PlusIcon size={16} className="mr-1" data-id="element-213" />
              Add Supplier
            </button>
            {currentStage === 'collecting' && <button className="w-full mt-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={handleCompleteCollection} data-id="element-214">
                Complete Collection Phase
              </button>}
          </div>
        </div>
        {/* Right column - AI Recommendation */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-215">
          <div className="p-4 border-b border-gray-200" data-id="element-216">
            <h2 className="font-semibold" data-id="element-217">
              {currentStage === 'evaluation' || currentStage === 'award' ? 'AI Recommendation' : 'Event Summary'}
            </h2>
          </div>
          <div className="p-4" data-id="element-218">
            {currentStage === 'collecting' && <div data-id="element-219">
                <div className="mb-4" data-id="element-220">
                  <h3 className="text-sm font-medium text-gray-500 uppercase" data-id="element-221">
                    Event Status
                  </h3>
                  <p className="mt-1 text-gray-800" data-id="element-222">
                    Collecting supplier proposals
                  </p>
                </div>
                <div className="mb-4" data-id="element-223">
                  <h3 className="text-sm font-medium text-gray-500 uppercase" data-id="element-224">
                    Deadline
                  </h3>
                  <p className="mt-1 text-gray-800" data-id="element-225">
                    {sourcingEvent.requirements.deadline ? sourcingEvent.requirements.deadline instanceof Date ? sourcingEvent.requirements.deadline.toLocaleDateString() : typeof sourcingEvent.requirements.deadline === 'string' ? new Date(sourcingEvent.requirements.deadline).toLocaleDateString() : '7 days from now' : '7 days from now'}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-md" data-id="element-226">
                  <p className="text-sm text-blue-800" data-id="element-227">
                    Waiting for suppliers to submit their proposals. You'll be
                    notified when new bids are received.
                  </p>
                </div>
              </div>}
            {(currentStage === 'evaluation' || currentStage === 'award') && <div data-id="element-228">
                {/* Requirements Comparison Section */}
                <div className="mb-4" data-id="element-229">
                  <h3 className="text-sm font-medium text-gray-500 uppercase mb-2" data-id="element-230">
                    Requirements Alignment
                  </h3>
                  <div className="space-y-3" data-id="element-231">
                    <div className="p-3 border border-gray-200 rounded-md" data-id="element-232">
                      <div className="flex justify-between" data-id="element-233">
                        <span className="text-sm font-medium" data-id="element-234">
                          Budget Target
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-235">
                          €
                          {sourcingEvent.requirements.budget?.toLocaleString() || 'Not specified'}
                        </span>
                      </div>
                      {bestBid && <div className="mt-2 flex items-center" data-id="element-236">
                          <div className={`h-2 rounded-full ${bestBid.bid.totalCost <= (sourcingEvent.requirements.budget || Infinity) ? 'bg-green-500' : 'bg-red-500'} flex-grow`} data-id="element-237"></div>
                          <span className="ml-2 text-xs" data-id="element-238">
                            {bestBid.bid.totalCost <= (sourcingEvent.requirements.budget || Infinity) ? `${Math.round((1 - bestBid.bid.totalCost / (sourcingEvent.requirements.budget || bestBid.bid.totalCost)) * 100)}% under budget` : `${Math.round((bestBid.bid.totalCost / (sourcingEvent.requirements.budget || 1) - 1) * 100)}% over budget`}
                          </span>
                        </div>}
                    </div>
                    <div className="p-3 border border-gray-200 rounded-md" data-id="element-239">
                      <div className="flex justify-between" data-id="element-240">
                        <span className="text-sm font-medium" data-id="element-241">
                          Delivery Target
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-242">
                          {sourcingEvent.requirements.deadline ? sourcingEvent.requirements.deadline instanceof Date ? sourcingEvent.requirements.deadline.toLocaleDateString() : typeof sourcingEvent.requirements.deadline === 'string' ? new Date(sourcingEvent.requirements.deadline).toLocaleDateString() : 'Not specified' : 'Not specified'}
                        </span>
                      </div>
                      {bestBid && <div className="mt-2 flex items-center" data-id="element-243">
                          {/* Calculate if delivery date meets the deadline */}
                          {(() => {
                      // Get the deadline as a Date object
                      const deadlineDate = sourcingEvent.requirements.deadline instanceof Date ? sourcingEvent.requirements.deadline : typeof sourcingEvent.requirements.deadline === 'string' ? new Date(sourcingEvent.requirements.deadline) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                      // Get the delivery date
                      const deliveryDate = bestBid.bid.deliveryDate;
                      // Check if delivery is on time
                      const isOnTime = deliveryDate <= deadlineDate;
                      // Calculate days difference for display
                      const diffTime = Math.abs(deliveryDate.getTime() - deadlineDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return <>
                                <div className={`h-2 rounded-full ${isOnTime ? 'bg-green-500' : diffDays > 3 ? 'bg-red-500' : 'bg-yellow-500'} flex-grow`} data-id="element-244"></div>
                                <span className="ml-2 text-xs" data-id="element-245">
                                  {isOnTime ? `On time (${diffDays} days before deadline)` : `${diffDays} days after deadline`}
                                </span>
                              </>;
                    })()}
                        </div>}
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-md mb-4" data-id="element-246">
                  <div className="flex items-start" data-id="element-247">
                    <div className="bg-green-100 rounded-full p-2 mr-3" data-id="element-248">
                      <ThumbsUpIcon size={16} className="text-green-600" data-id="element-249" />
                    </div>
                    <div data-id="element-250">
                      <h3 className="font-medium text-green-800" data-id="element-251">
                        Recommended Supplier
                      </h3>
                      <p className="text-sm text-green-700 mt-1" data-id="element-252">
                        Based on your requirements, we recommend:
                      </p>
                    </div>
                  </div>
                </div>
                {bestBid ? <div className="border border-green-200 rounded-md p-4 bg-white" data-id="element-253">
                    <h3 className="font-medium text-gray-800" data-id="element-254">
                      {bestBid.name}
                    </h3>
                    <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-255">
                      <div data-id="element-256">
                        <p className="text-xs text-gray-500" data-id="element-257">Unit Price</p>
                        <p className="text-sm font-medium text-green-600" data-id="element-258">
                          €{bestBid.bid?.unitPrice}
                        </p>
                      </div>
                      <div data-id="element-259">
                        <p className="text-xs text-gray-500" data-id="element-260">Total Cost</p>
                        <p className="text-sm font-medium" data-id="element-261">
                          €{bestBid.bid?.totalCost?.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-262">
                        <p className="text-xs text-gray-500" data-id="element-263">Delivery Date</p>
                        <p className="text-sm font-medium" data-id="element-264">
                          {bestBid.bid?.deliveryDate?.toLocaleDateString()}
                        </p>
                      </div>
                      <div data-id="element-265">
                        <p className="text-xs text-gray-500" data-id="element-266">Warranty</p>
                        <p className="text-sm font-medium" data-id="element-267">
                          {bestBid.bid?.warranty}
                        </p>
                      </div>
                    </div>
                    {/* Negotiation Section */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-md" data-id="element-268">
                      <div className="flex items-start" data-id="element-269">
                        <ZapIcon size={16} className="text-blue-600 mr-2 mt-0.5" data-id="element-270" />
                        <div data-id="element-271">
                          <p className="text-sm font-medium text-blue-800" data-id="element-272">
                            Negotiation Opportunity
                          </p>
                          <p className="text-xs text-blue-700 mt-1" data-id="element-273">
                            AI suggests negotiating a 5% discount and extended
                            warranty for bulk purchase.
                          </p>
                          <button className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700" onClick={handleStartNegotiation} data-id="element-274">
                            Start Negotiation
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-275">
                      <p className="text-xs text-gray-500" data-id="element-276">
                        Why this supplier?
                      </p>
                      <p className="text-sm mt-1" data-id="element-277">
                        This supplier offers the best value with the lowest unit
                        price while maintaining good delivery times and warranty
                        terms.
                      </p>
                    </div>
                    {/* Manual Override Section */}
                    {currentStage === 'evaluation' && <div className="mt-4 border-t border-gray-100 pt-3" data-id="element-278">
                        <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center" onClick={() => setManualOverride(!manualOverride)} data-id="element-279">
                          <span data-id="element-280">Select different supplier</span>
                          <ChevronDownIcon size={16} className={`ml-1 transition-transform ${manualOverride ? 'rotate-180' : ''}`} data-id="element-281" />
                        </button>
                        {manualOverride && <div className="mt-2 space-y-2" data-id="element-282">
                            {suppliers.filter(s => s.id !== bestBid.id && s.status === 'submitted').map(supplier => <button key={supplier.id} className="w-full p-2 border border-gray-200 rounded-md text-left hover:bg-gray-50 flex justify-between items-center" onClick={() => handleAward(supplier.id)} data-id="element-283">
                                  <span className="text-sm" data-id="element-284">
                                    {supplier.name}
                                  </span>
                                  <span className="text-xs text-gray-500" data-id="element-285">
                                    €{supplier.bid.unitPrice}
                                  </span>
                                </button>)}
                          </div>}
                      </div>}
                    {currentStage === 'evaluation' && <button className="w-full mt-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors" onClick={() => handleAward(bestBid.id)} data-id="element-286">
                        Award to this Supplier
                      </button>}
                    {currentStage === 'award' && <div className="w-full mt-4 py-2 bg-green-100 text-center rounded-md" data-id="element-287">
                        <div className="flex items-center justify-center text-green-800" data-id="element-288">
                          <CheckIcon size={16} className="mr-1" data-id="element-289" />
                          <span data-id="element-290">Awarded to this Supplier</span>
                        </div>
                      </div>}
                  </div> : <div className="border border-gray-200 rounded-md p-4 bg-gray-50" data-id="element-291">
                    <p className="text-gray-600" data-id="element-292">
                      No suppliers available for recommendation.
                    </p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>
      {/* Add Supplier Modal */}
      {showSupplierModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-293">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-294">
            <div className="flex justify-between items-center mb-4" data-id="element-295">
              <h3 className="text-lg font-semibold" data-id="element-296">Add Supplier</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowSupplierModal(false)} data-id="element-297">
                <XIcon size={20} data-id="element-298" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-299">
              <div data-id="element-300">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-301">
                  Supplier Name*
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={newSupplierName} onChange={e => setNewSupplierName(e.target.value)} placeholder="Enter supplier name" data-id="element-302" />
              </div>
              <div data-id="element-303">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-304">
                  Email Address
                </label>
                <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={newSupplierEmail} onChange={e => setNewSupplierEmail(e.target.value)} placeholder="Enter supplier email" data-id="element-305" />
              </div>
              <div className="flex justify-end space-x-2 mt-6" data-id="element-306">
                <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={() => setShowSupplierModal(false)} data-id="element-307">
                  Cancel
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleAddSupplier} disabled={!newSupplierName.trim()} data-id="element-308">
                  Add Supplier
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Proposal View Modal */}
      {showProposalModal && selectedProposal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-309">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-310">
            <div className="flex justify-between items-center mb-4" data-id="element-311">
              <h3 className="text-lg font-semibold" data-id="element-312">
                Proposal from {selectedProposal.name}
              </h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowProposalModal(false)} data-id="element-313">
                <XIcon size={20} data-id="element-314" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-315">
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50 mb-4" data-id="element-316">
                <h4 className="font-medium text-gray-800 mb-2" data-id="element-317">Bid Details</h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-318">
                  <div data-id="element-319">
                    <p className="text-sm text-gray-500" data-id="element-320">Unit Price</p>
                    <p className="text-lg font-medium" data-id="element-321">
                      €{selectedProposal.bid?.unitPrice}
                    </p>
                  </div>
                  <div data-id="element-322">
                    <p className="text-sm text-gray-500" data-id="element-323">Total Cost</p>
                    <p className="text-lg font-medium" data-id="element-324">
                      €{selectedProposal.bid?.totalCost?.toLocaleString()}
                    </p>
                  </div>
                  <div data-id="element-325">
                    <p className="text-sm text-gray-500" data-id="element-326">Delivery Date</p>
                    <p className="text-lg font-medium" data-id="element-327">
                      {selectedProposal.bid?.deliveryDate?.toLocaleDateString()}
                    </p>
                  </div>
                  <div data-id="element-328">
                    <p className="text-sm text-gray-500" data-id="element-329">Warranty</p>
                    <p className="text-lg font-medium" data-id="element-330">
                      {selectedProposal.bid?.warranty}
                    </p>
                  </div>
                </div>
              </div>
              {/* Proposal Document Viewer */}
              <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-331">
                <div className="bg-gray-100 p-3 flex justify-between items-center" data-id="element-332">
                  <div className="flex items-center" data-id="element-333">
                    <FileTextIcon size={18} className="text-blue-600 mr-2" data-id="element-334" />
                    <span className="font-medium" data-id="element-335">
                      {selectedProposal.bid?.proposalFile}
                    </span>
                  </div>
                  <a href="#" className="text-blue-600 hover:text-blue-800 flex items-center" onClick={e => e.preventDefault()} data-id="element-336">
                    <span className="text-sm" data-id="element-337">Open</span>
                    <ExternalLinkIcon size={14} className="ml-1" data-id="element-338" />
                  </a>
                </div>
                <div className="p-4 min-h-[300px] bg-gray-50 flex items-center justify-center" data-id="element-339">
                  <div className="text-center" data-id="element-340">
                    <FileTextIcon size={48} className="text-gray-400 mx-auto mb-2" data-id="element-341" />
                    <p className="text-gray-500" data-id="element-342">
                      Proposal document preview would appear here
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-343">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => setShowProposalModal(false)} data-id="element-344">
                Close
              </button>
            </div>
          </div>
        </div>}
      {/* Risk Assessment Modal */}
      {showRiskModal && selectedRiskSupplier && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-345">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-346">
            <div className="flex justify-between items-center mb-4" data-id="element-347">
              <h3 className="text-lg font-semibold" data-id="element-348">
                Risk Assessment: {selectedRiskSupplier.name}
              </h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowRiskModal(false)} data-id="element-349">
                <XIcon size={20} data-id="element-350" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-351">
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50 mb-4" data-id="element-352">
                <h4 className="font-medium text-gray-800 mb-3" data-id="element-353">
                  Overall Risk Level
                </h4>
                <div className="flex items-center mb-4" data-id="element-354">
                  <ShieldCheckIcon size={20} className={`mr-2 ${selectedRiskSupplier.bid?.riskLevel === 'Low' ? 'text-green-500' : selectedRiskSupplier.bid?.riskLevel === 'Medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-355" />
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskBadgeColor(selectedRiskSupplier.bid?.riskLevel)}`} data-id="element-356">
                    {selectedRiskSupplier.bid?.riskLevel} Risk
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3" data-id="element-357">
                  {selectedRiskSupplier.bid?.riskDetails.analysis}
                </p>
              </div>
              <div className="border border-gray-200 rounded-md p-4 mb-4" data-id="element-358">
                <h4 className="font-medium text-gray-800 mb-3" data-id="element-359">
                  Risk Categories
                </h4>
                <div className="space-y-4" data-id="element-360">
                  <div className="flex items-start" data-id="element-361">
                    <div className="w-6 h-6 mt-0.5 mr-3 flex-shrink-0" data-id="element-362">
                      {getRiskIcon(selectedRiskSupplier.bid?.riskDetails.financial)}
                    </div>
                    <div data-id="element-363">
                      <h5 className="text-sm font-medium flex items-center" data-id="element-364">
                        Financial Stability
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getRiskBadgeColor(selectedRiskSupplier.bid?.riskDetails.financial)}`} data-id="element-365">
                          {selectedRiskSupplier.bid?.riskDetails.financial}
                        </span>
                      </h5>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-366">
                        {selectedRiskSupplier.bid?.riskDetails.financial === 'Low' ? 'Strong financial indicators with consistent profitability and liquidity.' : selectedRiskSupplier.bid?.riskDetails.financial === 'Medium' ? 'Adequate financial health with some fluctuations in recent quarters.' : 'Financial concerns including potential cash flow issues and inconsistent profitability.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-367">
                    <div className="w-6 h-6 mt-0.5 mr-3 flex-shrink-0" data-id="element-368">
                      {getRiskIcon(selectedRiskSupplier.bid?.riskDetails.delivery)}
                    </div>
                    <div data-id="element-369">
                      <h5 className="text-sm font-medium flex items-center" data-id="element-370">
                        Delivery Performance
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getRiskBadgeColor(selectedRiskSupplier.bid?.riskDetails.delivery)}`} data-id="element-371">
                          {selectedRiskSupplier.bid?.riskDetails.delivery}
                        </span>
                      </h5>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-372">
                        {selectedRiskSupplier.bid?.riskDetails.delivery === 'Low' ? 'Excellent on-time delivery history with robust logistics capabilities.' : selectedRiskSupplier.bid?.riskDetails.delivery === 'Medium' ? 'Generally reliable delivery with occasional delays in peak periods.' : 'Inconsistent delivery performance with multiple late deliveries in past projects.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-373">
                    <div className="w-6 h-6 mt-0.5 mr-3 flex-shrink-0" data-id="element-374">
                      {getRiskIcon(selectedRiskSupplier.bid?.riskDetails.quality)}
                    </div>
                    <div data-id="element-375">
                      <h5 className="text-sm font-medium flex items-center" data-id="element-376">
                        Quality Assurance
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getRiskBadgeColor(selectedRiskSupplier.bid?.riskDetails.quality)}`} data-id="element-377">
                          {selectedRiskSupplier.bid?.riskDetails.quality}
                        </span>
                      </h5>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-378">
                        {selectedRiskSupplier.bid?.riskDetails.quality === 'Low' ? 'Robust quality management system with ISO certifications and low defect rates.' : selectedRiskSupplier.bid?.riskDetails.quality === 'Medium' ? 'Acceptable quality standards with occasional minor issues that were addressed promptly.' : 'History of quality issues with inconsistent adherence to specifications.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-379">
                    <div className="w-6 h-6 mt-0.5 mr-3 flex-shrink-0" data-id="element-380">
                      {getRiskIcon(selectedRiskSupplier.bid?.riskDetails.compliance)}
                    </div>
                    <div data-id="element-381">
                      <h5 className="text-sm font-medium flex items-center" data-id="element-382">
                        Regulatory Compliance
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getRiskBadgeColor(selectedRiskSupplier.bid?.riskDetails.compliance)}`} data-id="element-383">
                          {selectedRiskSupplier.bid?.riskDetails.compliance}
                        </span>
                      </h5>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-384">
                        {selectedRiskSupplier.bid?.riskDetails.compliance === 'Low' ? 'Strong compliance record with all relevant regulations and industry standards.' : selectedRiskSupplier.bid?.riskDetails.compliance === 'Medium' ? 'Generally compliant with minor historical issues that have been resolved.' : 'Multiple compliance concerns including recent regulatory violations.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4" data-id="element-385">
                <h4 className="font-medium text-gray-800 mb-3" data-id="element-386">
                  Risk Mitigation Recommendations
                </h4>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-387">
                  {selectedRiskSupplier.bid?.riskLevel === 'Low' ? <>
                      <li className="flex items-start" data-id="element-388">
                        <CheckCircleIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-389" />
                        <span data-id="element-390">
                          Standard contract terms are sufficient for this
                          low-risk supplier.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-391">
                        <CheckCircleIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-392" />
                        <span data-id="element-393">
                          Maintain regular performance reviews on a quarterly
                          basis.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-394">
                        <CheckCircleIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-395" />
                        <span data-id="element-396">
                          Consider longer-term strategic partnership
                          opportunities.
                        </span>
                      </li>
                    </> : selectedRiskSupplier.bid?.riskLevel === 'Medium' ? <>
                      <li className="flex items-start" data-id="element-397">
                        <AlertTriangleIcon size={16} className="text-yellow-500 mr-2 mt-0.5" data-id="element-398" />
                        <span data-id="element-399">
                          Include more stringent delivery timeline requirements
                          in contract.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-400">
                        <AlertTriangleIcon size={16} className="text-yellow-500 mr-2 mt-0.5" data-id="element-401" />
                        <span data-id="element-402">
                          Implement monthly performance reviews during initial
                          engagement period.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-403">
                        <AlertTriangleIcon size={16} className="text-yellow-500 mr-2 mt-0.5" data-id="element-404" />
                        <span data-id="element-405">
                          Consider requiring additional quality assurance
                          documentation.
                        </span>
                      </li>
                    </> : <>
                      <li className="flex items-start" data-id="element-406">
                        <XCircleIcon size={16} className="text-red-500 mr-2 mt-0.5" data-id="element-407" />
                        <span data-id="element-408">
                          Require performance bond or financial guarantees in
                          contract.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-409">
                        <XCircleIcon size={16} className="text-red-500 mr-2 mt-0.5" data-id="element-410" />
                        <span data-id="element-411">
                          Implement stringent quality control checks and
                          inspection requirements.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-412">
                        <XCircleIcon size={16} className="text-red-500 mr-2 mt-0.5" data-id="element-413" />
                        <span data-id="element-414">
                          Develop contingency plans with alternative suppliers
                          identified.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-415">
                        <XCircleIcon size={16} className="text-red-500 mr-2 mt-0.5" data-id="element-416" />
                        <span data-id="element-417">
                          Consider phased approach with smaller initial order to
                          validate performance.
                        </span>
                      </li>
                    </>}
                </ul>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-418">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => setShowRiskModal(false)} data-id="element-419">
                Close
              </button>
            </div>
          </div>
        </div>}
      {/* Negotiation Modal */}
      {showNegotiationModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-420">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-421">
            <div className="flex justify-between items-center mb-4" data-id="element-422">
              <h3 className="text-lg font-semibold" data-id="element-423">
                {negotiationStage === 'initial' && 'Start Negotiation'}
                {negotiationStage === 'progress' && 'Negotiation in Progress'}
                {negotiationStage === 'complete' && 'Negotiation Results'}
              </h3>
              {negotiationStage !== 'progress' && <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowNegotiationModal(false)} data-id="element-424">
                  <XIcon size={20} data-id="element-425" />
                </button>}
            </div>
            {negotiationStage === 'initial' && <div className="space-y-4" data-id="element-426">
                <p className="text-gray-700" data-id="element-427">
                  Our AI will negotiate with {bestBid?.name} on your behalf to
                  secure better terms.
                </p>
                <div className="p-3 bg-blue-50 rounded-md" data-id="element-428">
                  <div className="flex items-start" data-id="element-429">
                    <ZapIcon size={16} className="text-blue-600 mr-2 mt-0.5" data-id="element-430" />
                    <div data-id="element-431">
                      <p className="text-sm font-medium text-blue-800" data-id="element-432">
                        Negotiation Strategy
                      </p>
                      <ul className="text-sm text-blue-700 mt-1 list-disc list-inside" data-id="element-433">
                        <li data-id="element-434">Request a 5-10% price discount</li>
                        <li data-id="element-435">Ask for extended warranty terms</li>
                        <li data-id="element-436">Negotiate for priority support</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-6" data-id="element-437">
                  <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={() => setShowNegotiationModal(false)} data-id="element-438">
                    Cancel
                  </button>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={simulateNegotiation} data-id="element-439">
                    Start Negotiation
                  </button>
                </div>
              </div>}
            {negotiationStage === 'progress' && <div className="space-y-4" data-id="element-440">
                <div className="flex flex-col items-center justify-center py-6" data-id="element-441">
                  <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full" data-id="element-442">
                    <ZapIcon size={32} className="text-blue-600 animate-pulse" data-id="element-443" />
                  </div>
                  <p className="mt-4 text-blue-800 font-medium" data-id="element-444">
                    AI is negotiating with the supplier...
                  </p>
                  <p className="text-sm text-gray-500 mt-2" data-id="element-445">
                    This may take a few moments
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-6" data-id="element-446">
                    <div className="bg-blue-600 h-2 rounded-full animate-[progress_3s_ease-in-out]" style={{
                width: '100%'
              }} data-id="element-447"></div>
                  </div>
                </div>
              </div>}
            {negotiationStage === 'complete' && negotiationResults && <div className="space-y-4" data-id="element-448">
                <div className="p-4 bg-green-50 rounded-md" data-id="element-449">
                  <div className="flex items-start" data-id="element-450">
                    <CheckCircleIcon size={20} className="text-green-600 mr-2" data-id="element-451" />
                    <div data-id="element-452">
                      <p className="font-medium text-green-800" data-id="element-453">
                        Negotiation Successful!
                      </p>
                      <p className="text-sm text-green-700 mt-1" data-id="element-454">
                        We've negotiated better terms with {bestBid?.name}.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-455">
                  <h4 className="font-medium text-gray-800 mb-3" data-id="element-456">
                    Negotiated Terms
                  </h4>
                  <div className="space-y-3" data-id="element-457">
                    <div className="flex justify-between items-center" data-id="element-458">
                      <span className="text-sm text-gray-600" data-id="element-459">
                        Original Price:
                      </span>
                      <span className="text-sm line-through" data-id="element-460">
                        €{negotiationResults.originalPrice}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-461">
                      <span className="text-sm text-gray-600" data-id="element-462">
                        Negotiated Price:
                      </span>
                      <span className="text-sm font-medium text-green-600" data-id="element-463">
                        €{negotiationResults.negotiatedPrice}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-464">
                      <span className="text-sm text-gray-600" data-id="element-465">Savings:</span>
                      <span className="text-sm font-medium text-green-600" data-id="element-466">
                        €{negotiationResults.discount} (
                        {Math.round(negotiationResults.discount / negotiationResults.originalPrice * 100)}
                        %)
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-100" data-id="element-467">
                      <p className="text-sm text-gray-600 mb-2" data-id="element-468">
                        Additional Terms:
                      </p>
                      <ul className="text-sm space-y-1" data-id="element-469">
                        {negotiationResults.additionalTerms.map((term, index) => <li key={index} className="flex items-start" data-id="element-470">
                              <CheckIcon size={16} className="text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-471" />
                              <span data-id="element-472">{term}</span>
                            </li>)}
                      </ul>
                    </div>
                  </div>
                </div>
                {negotiationResults.accepted === null ? <div className="flex justify-end space-x-2 mt-6" data-id="element-473">
                    <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={handleDeclineNegotiation} data-id="element-474">
                      Decline
                    </button>
                    <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" onClick={handleAcceptNegotiation} data-id="element-475">
                      Accept Terms
                    </button>
                  </div> : <div className="mt-4 p-3 rounded-md text-center bg-gray-50" data-id="element-476">
                    {negotiationResults.accepted ? <p className="text-green-600 font-medium" data-id="element-477">
                        You've accepted the negotiated terms!
                      </p> : <p className="text-gray-600" data-id="element-478">
                        You've declined the negotiated terms.
                      </p>}
                  </div>}
              </div>}
          </div>
        </div>}
    </div>;
};
export default EventDashboard;